import React from 'react';

/**
 * RichText — a compact, safe renderer for short user/staff text (support
 * comments, replies). Handles the formatting people actually use in messages:
 *   **bold** / __bold__, *italic* / _italic_, "- " or "* " bullet lists, and
 *   line breaks.
 *
 * It is NOT a full markdown engine (no headings, links, code blocks) — that
 * would render oversized inside a small comment bubble. Input is treated as
 * plain text and only these inline patterns are turned into elements, so there
 * is no raw-HTML injection surface (nothing uses dangerouslySetInnerHTML).
 */

function renderInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // bold (**x** / __x__) first, then italic (*x* / _x_)
  const re = /(\*\*|__)(.+?)\1|(\*|_)(.+?)\3/g;
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] !== undefined) nodes.push(<strong key={key++}>{m[2]}</strong>);
    else nodes.push(<em key={key++}>{m[4]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export const RichText: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  const lines = (text || '').split('\n');
  const blocks: React.ReactNode[] = [];
  let bullets: string[] = [];

  const flushBullets = (key: string | number) => {
    if (bullets.length) {
      const items = bullets;
      blocks.push(
        <ul key={`ul-${key}`} className="list-disc ps-5 space-y-0.5">
          {items.map((b, i) => (
            <li key={i}>{renderInline(b)}</li>
          ))}
        </ul>
      );
      bullets = [];
    }
  };

  lines.forEach((line, i) => {
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    if (bullet) {
      bullets.push(bullet[1]);
      return;
    }
    flushBullets(i);
    if (line.trim() === '') {
      blocks.push(<div key={`sp-${i}`} className="h-1.5" aria-hidden />);
    } else {
      blocks.push(<p key={`p-${i}`}>{renderInline(line)}</p>);
    }
  });
  flushBullets('end');

  return <div className={className}>{blocks}</div>;
};

export default RichText;
